import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaBars, FaTimes } from "react-icons/fa";
import "./App.css";
import ElderLogin from "./components/elderLogin";
import ElderRegister from "./components/elderRegister";
import ElderProfileSetup from "./components/elderProfileSetup";
import ElderIntroPage from "./components/elderIntroPage";
import ElderMap from "./components/eldermap";
import ElderHome from "./components/elderHome";
import Logout from "./components/logout";
import LanguageSwitcher from "./components/LanguageSwitcher";
import ElderMarketingPage from "./components/ElderMarketingPage";
import PasswordResetPage from "./components/PasswordResetPage";
import logo from "./images/cape.png";

const ElderDashboard = React.lazy(() => import("./components/ElderDashboard"));

const AppContent = () => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const isMarketingPage = location.pathname === "/elder-marketing";
  const isSignedIn = Boolean(
    localStorage.getItem("patientID") && localStorage.getItem("token")
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const closeOutsideHeader = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOutsideHeader);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOutsideHeader);
    };
  }, [menuOpen]);

  const navigationClass = ({ isActive }) =>
    `nav-link${isActive ? " nav-link-active" : ""}`;

  return (
    <div className="app-shell">
      <a className="skip-link" href="#elder-main-content">Skip to content</a>
      {isMarketingPage ? (
        <header className="topbar topbar-minimal">
          <Link to="/" className="topbar-brand topbar-brand-minimal" aria-label="Go to home page">
            <img src={logo} alt="UberCARE for Elders logo" className="topbar-logo" />
            <span className="topbar-brand-copy">
              <span className="topbar-kicker">Care that feels easier to reach</span>
              <span className="topbar-title">{t("nav.brand")}</span>
            </span>
          </Link>
        </header>
      ) : (
        <header ref={headerRef} className="topbar">
          <Link to="/" className="topbar-brand">
            <img src={logo} alt="UberCARE for Elders logo" className="topbar-logo" />
            <span className="topbar-brand-copy">
              <span className="topbar-kicker">Care that feels easier to reach</span>
              <span className="topbar-title">{t("nav.brand")}</span>
            </span>
          </Link>

          <button
            type="button"
            className="topbar-menu-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="elder-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
            <span>{menuOpen ? "Close" : "Menu"}</span>
          </button>

          <nav
            id="elder-navigation"
            className={`topbar-links${menuOpen ? " topbar-links-open" : ""}`}
            aria-label="Primary navigation"
          >
            <NavLink className={navigationClass} to="/" end>
              {t("nav.home")}
            </NavLink>
            {isSignedIn ? (
              <>
                <NavLink className={navigationClass} to="/elder-intro">
                  Overview
                </NavLink>
                <NavLink className={navigationClass} to="/elder-map">
                  Caregiver map
                </NavLink>
                <NavLink className={navigationClass} to="/elder-dashboard">
                  Care dashboard
                </NavLink>
                <Logout />
              </>
            ) : (
              <>
                <NavLink className={navigationClass} to="/elder-login">
                  {t("nav.elderLogin")}
                </NavLink>
                <NavLink className={navigationClass} to="/elder-register">
                  {t("nav.elderRegister")}
                </NavLink>
              </>
            )}
            <LanguageSwitcher />
          </nav>
        </header>
      )}

      <main id="elder-main-content" className="page-offset">
        <Routes>
          <Route path="/" element={<ElderHome />} />
          <Route path="/elder-home" element={<Navigate to="/" replace />} />
          <Route path="/elder-login" element={<ElderLogin />} />
          <Route path="/elder-register" element={<ElderRegister />} />
          <Route path="/forgot-password" element={<PasswordResetPage />} />
          <Route path="/elder-profile-setup" element={<ElderProfileSetup />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/elder-map" element={<ElderMap />} />
          <Route
            path="/elder-dashboard"
            element={(
              <Suspense fallback={<div className="page-shell page-panel page-content">Loading your care dashboard…</div>}>
                <ElderDashboard />
              </Suspense>
            )}
          />
          <Route path="/elder-intro" element={<ElderIntroPage />} />
          <Route path="/elder-marketing" element={<ElderMarketingPage />} />
        </Routes>
        <footer className="global-footer" aria-label="UberCARE product information">
          <strong>UberCARE for Elders</strong>
          <span>Care across borders, designed with clarity and dignity.</span>
          <span>© {new Date().getFullYear()} UberCARE</span>
        </footer>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <Router basename="/elder">
      <AppContent />
    </Router>
  );
};

export default App;
